import React, { useCallback, useEffect, useState } from "react";
import { Badge, Button, Card, Spinner, Table } from "react-bootstrap";
import { getMyIncentiveHeadline, getMyIncentiveMonth } from "../services/leadTrackerApi";

const money = (value) => `₹${Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const travellers = (value) => Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
const callTime = (value) => {
  const totalSeconds = Math.max(0, Number(value || 0));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
};

const monthLabel = (period) => {
  if (!period) return "";
  const [year, month] = period.split("-").map(Number);
  return new Date(year, month - 1, 1).toLocaleDateString("en-IN", { month: "long", year: "numeric" });
};

/**
 * The member's own incentive, on their dashboard. Everything is computed server side and
 * arrives as one small payload - no conversion rows are pulled into the page.
 */
export default function MyIncentiveCard({ team, token }) {
  const [headline, setHeadline] = useState(null);
  const [detail, setDetail] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async (silent = false) => {
    if (!token) return;
    if (!silent) setLoading(true);
    try {
      setHeadline(await getMyIncentiveHeadline(team, token));
      setError("");
    } catch (err) {
      setError(err?.response?.data?.message || "Could not load your incentive.");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [team, token]);

  useEffect(() => {
    load();
    if (team !== "CALL") return undefined;

    const timer = window.setInterval(() => load(true), 120000);
    return () => window.clearInterval(timer);
  }, [load, team]);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (!next || detail || !headline?.period) return;
    setLoadingDetail(true);
    try {
      setDetail(await getMyIncentiveMonth(team, token, headline.period));
    } catch (err) {
      setError(err?.response?.data?.message || "Could not load the breakdown.");
    } finally {
      setLoadingDetail(false);
    }
  };

  if (loading) {
    return (
      <Card className="incentive-card mb-3">
        <Card.Body className="py-3 incentive-sub"><Spinner size="sm" className="me-2" />Loading your incentive...</Card.Body>
      </Card>
    );
  }
  if (error && !headline) {
    return <Card className="mb-3"><Card.Body className="py-3 small text-danger">{error}</Card.Body></Card>;
  }
  if (!headline) return null;

  const counted = Number(headline.travellersCounted || 0);
  const boundary = headline.nextBracketAt == null ? null : Number(headline.nextBracketAt);
  const toNext = boundary == null ? null : Math.max(0, boundary - counted);
  const progress = boundary == null || boundary <= 0
    ? null
    : Math.min(100, Math.round((counted / boundary) * 100));
  // Zero and null both mean no target has been set for this member.
  const target = Number(headline.travellerTarget) > 0 ? Number(headline.travellerTarget) : null;
  const targetProgress = target == null || target <= 0
    ? null
    : Math.min(100, Math.round((counted / target) * 100));
  const targetLeft = target == null ? null : Math.max(0, target - counted);

  return (
    <Card className="incentive-card mb-3">
      <Card.Body className="py-3">
        <div className="d-flex flex-wrap align-items-center gap-3">
          <div className="me-auto">
            <div className="incentive-label">Incentive earned · {monthLabel(headline.period)}</div>
            <div className="incentive-amount">{money(headline.net)}</div>
            <div className="incentive-sub">
              {travellers(counted)} chargeable persons counted
              {headline.currentRate != null && <> · earning {money(headline.currentRate)} per chargeable person</>}
            </div>
          </div>

          {toNext != null && (
            <div className="incentive-chip">
              <div className="incentive-chip-label">Next bracket</div>
              <div className="incentive-chip-value">
                {toNext > 0
                  ? <>{travellers(toNext)} more for {money(headline.nextBracketRate)}</>
                  : <>Now at {money(headline.nextBracketRate)}</>}
              </div>
              {progress != null && (
                <div className="incentive-progress"><span style={{ width: `${progress}%` }} /></div>
              )}
            </div>
          )}

          {target != null && (
            <div className="incentive-chip">
              <div className="incentive-chip-label">Monthly target</div>
              <div className="incentive-chip-value">
                {travellers(counted)} of {target} chargeable persons
                {targetProgress != null && <span className="incentive-chip-note"> · {targetProgress}%</span>}
              </div>
              {targetProgress != null && (
                <div className={`incentive-progress ${targetLeft === 0 ? "is-met" : ""}`}>
                  <span style={{ width: `${targetProgress}%` }} />
                </div>
              )}
              <div className="incentive-chip-note">
                {targetLeft === 0 ? "Target met" : `${travellers(targetLeft)} to go`}
              </div>
            </div>
          )}

          {team === "CALL" && (
            <div className="incentive-chip">
              <div className="incentive-chip-label">Today's call time</div>
              <div className="incentive-chip-value">{callTime(headline.todayCallSeconds)}</div>
            </div>
          )}

          <div className="incentive-chip">
            <div className="incentive-chip-label">Earned to date</div>
            <div className="incentive-chip-value">{money(headline.lifetimeNet)}</div>
          </div>

          <Button size="sm" variant="outline-success" className="incentive-toggle" onClick={toggle}>
            {open ? "Hide breakdown" : "Show breakdown"}
          </Button>
        </div>

        {open && (
          <div className="incentive-breakdown">
            {loadingDetail && <div className="small incentive-sub"><Spinner size="sm" className="me-2" />Loading breakdown...</div>}
            {!loadingDetail && detail && (
              <div className="table-responsive">
                <Table size="sm" borderless className="small mb-0 align-middle">
                  <thead>
                    <tr className="text-muted">
                      <th>Booking</th>
                      <th>Trip</th>
                      <th className="text-end">Chargeable Persons</th>
                      <th className="text-end">Your share</th>
                      <th className="text-end">Earned</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(detail.lines || []).map((line) => (
                      <tr key={line.bookingId}>
                        <td>#{line.bookingId}</td>
                        <td>{line.tripTitle || "-"}</td>
                        <td className="text-end">{line.travellers ?? "-"}</td>
                        <td className="text-end">
                          {travellers(line.travellerShare)}
                          {line.contributionPercent != null && line.contributionPercent !== 100 && (
                            <span className="text-muted"> ({line.contributionPercent}%)</span>
                          )}
                        </td>
                        <td className="text-end fw-semibold">{money(line.amount)}</td>
                      </tr>
                    ))}
                    {(detail.adjustmentLines || []).map((line) => (
                      <tr key={`adj-${line.bookingId}`}>
                        <td>#{line.bookingId}</td>
                        <td colSpan={3} className="text-muted">
                          Cancelled after {monthLabel(line.originPeriod)} was paid
                          <Badge bg="secondary" className="ms-2 fw-normal">Adjustment</Badge>
                        </td>
                        <td className="text-end fw-semibold text-danger">{money(line.amount)}</td>
                      </tr>
                    ))}
                    {!detail.lines?.length && !detail.adjustmentLines?.length && (
                      <tr><td colSpan={5} className="text-center text-muted">No conversions counted yet this month.</td></tr>
                    )}
                  </tbody>
                </Table>
              </div>
            )}
          </div>
        )}
      </Card.Body>
    </Card>
  );
}
